import React from 'react';
import { Avatar, Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import * as MuiIcons from '@mui/icons-material';
import { getTileIcon } from '../../utils/logoDetection';

interface IconDisplayProps {
  icon: string;
  iconType?: 'material' | 'service' | 'custom';
  iconUrl?: string;
  url?: string;
  title?: string;
  size?: number;
  sx?: SxProps<Theme>;
  fallbackIcon?: string;
}

const IconDisplay: React.FC<IconDisplayProps> = ({
  icon,
  iconType = 'material',
  iconUrl,
  url,
  title,
  size = 24,
  sx,
  fallbackIcon = 'Link'
}) => {
  // If no specific icon type is provided, try to auto-detect
  const resolvedIcon = React.useMemo(() => {
    if (iconType === 'custom' && iconUrl) {
      return { type: 'custom' as const, value: iconUrl };
    }
    
    if (iconType === 'service' && iconUrl) {
      return { type: 'service' as const, value: iconUrl };
    }
    
    // Auto-detect based on URL/title if available (includes favicon detection)
    if (url || title) {
      return getTileIcon(url || '', title, icon);
    }
    
    // Default to material icon if no URL is available
    return { type: 'material' as const, value: icon };
  }, [icon, iconType, iconUrl, url, title]);

  // Render material icon
  if (resolvedIcon.type === 'material') {
    const iconName = resolvedIcon.value;
    
    // Try multiple case variations for better matching
    let IconComponent = (MuiIcons as any)[iconName];
    if (!IconComponent) {
      // Try with first letter capitalized
      const capitalizedName = iconName.charAt(0).toUpperCase() + iconName.slice(1);
      IconComponent = (MuiIcons as any)[capitalizedName];
    }
    if (!IconComponent) {
      // Try fallback icon
      IconComponent = (MuiIcons as any)[fallbackIcon];
    }
    if (!IconComponent) {
      // Final fallback
      IconComponent = MuiIcons.Link;
    }
    
    return (
      <Box
        component={IconComponent}
        sx={{
          fontSize: size,
          ...sx
        }}
      />
    );
  }
  
  // Render service or custom image
  if (resolvedIcon.type === 'service' || resolvedIcon.type === 'custom') {
    return (
      <Avatar
        src={resolvedIcon.value}
        sx={{
          width: size,
          height: size,
          bgcolor: 'transparent',
          ...sx
        }}
        variant="rounded"
      >
        {/* Fallback content if image fails */}
        {(() => {
          const fallbackIconName = (resolvedIcon as any).fallback || fallbackIcon;
          const FallbackIconComponent = (MuiIcons as any)[fallbackIconName] || MuiIcons.Link;
          return React.createElement(FallbackIconComponent, { sx: { fontSize: size * 0.6 } });
        })()}
      </Avatar>
    );
  }
  
  // Default fallback
  const FallbackIcon = (MuiIcons as any)[fallbackIcon] || MuiIcons.Link;
  return (
    <Box
      component={FallbackIcon}
      sx={{
        fontSize: size,
        ...sx
      }}
    />
  );
};

export default IconDisplay;